import { cn } from '@amore/ui/cn'
import { Alert } from '@amore/ui/components/alert'
import { Avatar } from '@amore/ui/components/avatar'
import { Button } from '@amore/ui/components/button'
import { Card } from '@amore/ui/components/card'
import { Select, SelectItem } from '@amore/ui/components/select'
import {
  SortableDataList,
  SortableDataListHandle,
  SortableDataListItem,
} from '@amore/ui/components/sortable-data-list'
import { ViewToggle, ViewToggleItem } from '@amore/ui/components/view-toggle'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import {
  ArrowDownUp,
  ChevronRight,
  Eye,
  List,
  MapPin,
  Plus,
  Send,
  Store,
  TriangleAlert,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { formatDate } from '@/lib/format'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { ScheduleEvent, WeddingData } from '@/mock/types'
import { ConfirmDialog } from '@/patterns/dialog'
import { EmptyState } from '@/patterns/empty-state'
import { FilterBar } from '@/patterns/filter-bar'
import { ItemMenu, type ItemMenuEntry } from '@/patterns/item-card'
import { PromptList, useAddPromptTask } from '@/patterns/prompts'
import { Screen } from '@/patterns/screen'
import { StatSummary } from '@/patterns/stat-summary'
import { useToast } from '@/shell/toaster'
import scheduleIllustration from './assets/schedule.svg'
import { EventSheet } from './event-sheet'
import { GuestView } from './guest-view'
import {
  filterLabels,
  formatDuration,
  type Gap,
  groupRange,
  isConflict,
  matchesFilter,
  moveEvent,
  ownerKey,
  ownerOf,
  type ScheduleFilter,
  type ScheduleGroup,
  scheduleFilters,
  scheduleGaps,
  scheduleGroups,
  scheduleOwners,
  scheduleSignature,
  sortByTime,
  timeRange,
  wholeDay,
} from './model'
import { schedulePrompts } from './prompts'
import { SendSheet } from './send-sheet'

type View = 'day' | 'guest'

const parseFilter = (value: string | undefined): ScheduleFilter =>
  scheduleFilters.includes(value as ScheduleFilter) ? (value as ScheduleFilter) : 'all'
const anyone = 'anyone'

const title = 'Harmonogram'
const description = 'Průběh svatebního dne od rána do noci, pro vás, tým i hosty.'

export function ScheduleScreen() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const addTask = useAddPromptTask()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as {
    filter?: string
    owner?: string
    view?: string
    as?: string
  }
  const { eventId } = useParams({ strict: false }) as { eventId?: string }
  const filter = parseFilter(search.filter)
  const owner = search.owner ?? anyone
  const view: View = search.view === 'guest' ? 'guest' : 'day'
  const guestAs = search.as ?? wholeDay
  // The new event's prefill; `{}` for a blank one.
  const [adding, setAdding] = useState<Partial<ScheduleEvent> | null>(null)
  const [sending, setSending] = useState(false)
  const [deleting, setDeleting] = useState<ScheduleEvent | null>(null)

  type ListSearch = { filter?: ScheduleFilter; owner?: string; view?: View; as?: string }
  const listSearch = (next: ListSearch = {}) => {
    const merged = { filter, owner, view, as: guestAs, ...next }
    return {
      ...(merged.filter !== 'all' ? { filter: merged.filter } : {}),
      ...(merged.owner !== anyone ? { owner: merged.owner } : {}),
      ...(merged.view !== 'day' ? { view: merged.view } : {}),
      ...(merged.view === 'guest' && merged.as !== wholeDay ? { as: merged.as } : {}),
    }
  }
  const setSearch = (next: ListSearch) =>
    void navigate({
      to: eventId ? `/schedule/${eventId}` : '/schedule',
      search: listSearch(next),
      replace: true,
    })
  const setFilter = (value: ScheduleFilter) => setSearch({ filter: value })
  const setOwner = (value: string) => setSearch({ owner: value })
  const setView = (value: View) => setSearch({ view: value })
  const openEvent = (event: ScheduleEvent) =>
    void navigate({ to: `/schedule/${event.id}`, search: listSearch() })
  const closeEvent = () => void navigate({ to: '/schedule', search: listSearch() })
  const addEvent = () => setAdding({})
  const openRecord = eventId ? data.schedule.find((event) => event.id === eventId) : undefined
  const sheets = (
    <>
      {openRecord ? (
        <EventSheet key={openRecord.id} event={openRecord} onClose={closeEvent} />
      ) : null}
      {adding ? <EventSheet initial={adding} onClose={() => setAdding(null)} /> : null}
      {sending ? <SendSheet onClose={() => setSending(false)} /> : null}
    </>
  )

  const setSchedule = (recipe: (schedule: ScheduleEvent[]) => ScheduleEvent[]) =>
    update((current) => ({ ...current, schedule: recipe(current.schedule) }))

  const active = data.schedule.filter((event) => !event.removed)
  const removed = data.schedule.filter((event) => event.removed)

  if (active.length === 0 && removed.length === 0)
    return (
      <Screen
        title={title}
        description={description}
        illustration={<img src={scheduleIllustration} alt="" className="h-auto w-full" />}
      >
        <EmptyState
          icon={<img src={scheduleIllustration} alt="" className="h-28 w-32" />}
          title="Harmonogram je zatím prázdný"
          description={
            canEdit
              ? 'Časy, které doplníte v sekcích Obřad, Jídlo a pití nebo Foto a video, se tu objeví samy. Můžete také přidat vlastní událost.'
              : 'Až pár doplní průběh dne, zobrazí se tady.'
          }
          actions={canEdit ? <Button onClick={addEvent}>Přidat první událost</Button> : null}
        />
        {sheets}
      </Screen>
    )

  const groups = scheduleGroups(data)
  const gaps = scheduleGaps(groups)
  const count = (value: ScheduleFilter) =>
    active.filter((event) => matchesFilter(event, value, gaps)).length
  const conflicts = count('conflicts')
  const untimed = count('untimed')
  const outOfOrder = [...gaps.values()].some((gap) => gap.type === 'order')
  // Suppliers holding a copy that no longer matches the schedule.
  const signature = scheduleSignature(data.schedule)
  const outdated = data.scheduleShares
    .filter((share) => share.signature !== signature)
    .map((share) => data.suppliers.find((supplier) => supplier.id === share.supplierId)?.name)
    .filter(Boolean)
  const ceremony = active.find((event) => event.source?.section === 'ceremony')
  const owners = scheduleOwners(data)
  // The tiles filter the day's list, so from the guest view they switch back to it.
  const toggleFilter = (value: ScheduleFilter) =>
    setSearch({ filter: filter === value ? 'all' : value, view: 'day' })
  // Reordering a filtered list would be guesswork: drag only in the full view.
  const sortable = canEdit && filter === 'all'

  const remove = (event: ScheduleEvent) => {
    if (!event.source) return setDeleting(event)
    setSchedule((schedule) => replaceById(schedule, event.id, { removed: true }))
    showToast({
      title: 'Událost odebrána',
      description: `${event.title} je v odebraných, sekce ${event.source.label} zůstává beze změny.`,
      action: {
        label: 'Vrátit zpět',
        altText: 'Vrátit událost do harmonogramu',
        onClick: () =>
          setSchedule((schedule) => replaceById(schedule, event.id, { removed: false })),
      },
    })
  }
  const toggleAudience = (event: ScheduleEvent) => {
    const audience = event.audience === 'guests' ? 'team' : 'guests'
    setSchedule((schedule) => replaceById(schedule, event.id, { audience }))
    showToast({
      title: audience === 'guests' ? 'Uvidí i hosté' : 'Skryto před hosty',
      description: event.title,
    })
  }
  const menuFor = (event: ScheduleEvent, group: ScheduleGroup): ItemMenuEntry[] => {
    const siblings = group.events
    const index = siblings.findIndex((item) => item.id === event.id)
    const before = siblings[index - 1]
    const after = siblings[index + 1]
    return [
      { label: 'Upravit událost', onSelect: () => openEvent(event) },
      {
        label: event.audience === 'guests' ? 'Skrýt před hosty' : 'Zobrazit hostům',
        onSelect: () => toggleAudience(event),
      },
      ...(sortable && before
        ? [
            {
              label: 'Posunout výše',
              onSelect: () => setSchedule((schedule) => moveEvent(schedule, event.id, before.id)),
            },
          ]
        : []),
      ...(sortable && after
        ? [
            {
              label: 'Posunout níže',
              onSelect: () => setSchedule((schedule) => moveEvent(schedule, event.id, after.id)),
            },
          ]
        : []),
      event.source
        ? { label: 'Odebrat z harmonogramu', onSelect: () => remove(event) }
        : { label: 'Smazat událost', onSelect: () => remove(event), tone: 'danger' as const },
    ]
  }

  const viewToggle = (
    <ViewToggle aria-label="Zobrazení harmonogramu">
      <ViewToggleItem
        icon={<List aria-hidden="true" />}
        pressed={view === 'day'}
        onClick={() => setView('day')}
      >
        Celý den
      </ViewToggleItem>
      <ViewToggleItem
        icon={<Eye aria-hidden="true" />}
        pressed={view === 'guest'}
        onClick={() => setView('guest')}
      >
        Pohled hosta
      </ViewToggleItem>
    </ViewToggle>
  )

  return (
    <Screen
      title={title}
      illustration={<img src={scheduleIllustration} alt="" className="h-auto w-full" />}
      description={description}
      actions={
        canEdit ? (
          <>
            <Button appearance="outline" tone="neutral" onClick={() => setSending(true)}>
              <Send className="size-4" aria-hidden="true" /> Poslat dodavatelům
            </Button>
            <Button onClick={addEvent}>
              <Plus className="size-4" aria-hidden="true" /> Přidat událost
            </Button>
          </>
        ) : null
      }
    >
      <StatSummary
        headline={{
          label: 'Svatební den',
          value: data.identity.date ? formatDate(data.identity.date, 'long') : 'Datum zatím není',
          caption: ceremony?.time ? `obřad ve ${ceremony.time}` : undefined,
        }}
        groups={[
          {
            title: 'Události',
            value: active.length,
            tiles: [
              {
                label: 'Pro hosty',
                value: count('guests'),
                caption: 'Uvidí je i hosté na webu',
                onSelect: () => toggleFilter('guests'),
                active: filter === 'guests',
              },
              {
                label: 'Jen tým',
                value: count('team'),
                caption: 'Příjezdy, přípravy, překvapení',
                onSelect: () => toggleFilter('team'),
                active: filter === 'team',
              },
            ],
          },
          {
            title: 'Ke kontrole',
            tiles: [
              {
                label: 'Bez času',
                value: untimed,
                caption: untimed ? 'Doplňte, až budete vědět' : 'Všechno má čas',
                tone: untimed ? 'warning' : 'success',
                onSelect: () => toggleFilter('untimed'),
                active: filter === 'untimed',
              },
              {
                label: 'Kolize',
                value: conflicts,
                caption: conflicts ? 'Překryv nebo pořadí' : 'Nic se nepřekrývá',
                tone: conflicts ? 'danger' : 'success',
                onSelect: () => toggleFilter('conflicts'),
                active: filter === 'conflicts',
              },
            ],
          },
        ]}
      />

      {canEdit && view === 'day' ? (
        <PromptList
          prompts={schedulePrompts(data).map(({ action, ...prompt }) => ({
            ...prompt,
            action:
              action.kind === 'event' ? (
                <Button
                  size="sm"
                  appearance="outline"
                  tone="neutral"
                  onClick={() => setAdding(action.event)}
                >
                  <Plus aria-hidden="true" className="size-3.5" />
                  Přidat do harmonogramu
                </Button>
              ) : (
                <Button
                  size="sm"
                  appearance="outline"
                  tone="neutral"
                  onClick={() => addTask(action.title, { labels: ['Harmonogram'] })}
                >
                  <Plus aria-hidden="true" className="size-3.5" />
                  Přidat úkol
                </Button>
              ),
          }))}
          onHide={(code) =>
            update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
          }
        />
      ) : null}

      {view === 'guest' ? null : (
        <FilterBar
          filters={scheduleFilters.map((value) => ({
            value,
            label: filterLabels[value],
            count: value === 'all' ? undefined : count(value),
          }))}
          value={filter}
          onChange={setFilter}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <Select
                aria-label="Zajišťuje"
                controlSize="sm"
                className="w-56"
                value={owner}
                onValueChange={setOwner}
              >
                <SelectItem value={anyone}>Zajišťuje kdokoli</SelectItem>
                {owners.map((item) => (
                  <SelectItem key={item.key} value={item.key}>
                    {item.name}
                  </SelectItem>
                ))}
              </Select>
              {viewToggle}
            </div>
          }
        />
      )}

      {view === 'guest' ? (
        <GuestView
          data={data}
          audienceKey={guestAs}
          onAudienceChange={(key) => setSearch({ as: key })}
          actions={viewToggle}
          onOpen={openEvent}
        />
      ) : (
        <>
          {canEdit && outdated.length ? (
            <Alert tone="info" announcement="none" className="flex items-center gap-3">
              <Send className="size-4 shrink-0" aria-hidden="true" />
              <span className="flex-1">
                Od odeslání se harmonogram změnil. Starší verzi má {outdated.join(', ')}.
              </span>
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => setSending(true)}
              >
                Poslat znovu
              </Button>
            </Alert>
          ) : null}
          {canEdit && outOfOrder ? (
            <Alert tone="warning" announcement="none" className="flex items-center gap-3">
              <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
              <span className="flex-1">Některé události nejdou časově po sobě.</span>
              <Button
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => {
                  setSchedule(sortByTime)
                  showToast({ title: 'Seřazeno podle času', tone: 'success' })
                }}
              >
                <ArrowDownUp className="size-4" aria-hidden="true" /> Seřadit podle času
              </Button>
            </Alert>
          ) : null}

          <Card padding="none" className="divide-y divide-border overflow-hidden">
            {groups.map((group) => {
              const visible = group.events.filter((event) => matchesFilter(event, filter, gaps))
              if (!visible.length) return null
              const range = groupRange(group.events)
              return (
                <section key={group.id ?? 'prep'} className="px-3 py-5 sm:px-6">
                  <header className="mb-2 flex items-baseline gap-3 pl-1">
                    <h2 className="font-heading text-xl tracking-tight">{group.label}</h2>
                    {range ? (
                      <span className="text-sm tabular-nums text-muted-foreground">{range}</span>
                    ) : null}
                  </header>
                  <SortableDataList
                    aria-label={`Události: ${group.label}`}
                    className="space-y-0"
                    items={visible.map((event) => event.id)}
                    onReorder={(from, to) => {
                      if (typeof from === 'string' && typeof to === 'string')
                        setSchedule((schedule) => moveEvent(schedule, from, to))
                    }}
                    accessibility={{
                      screenReaderInstructions: {
                        draggable:
                          'Mezerníkem událost zvednete, šipkami přesunete a mezerníkem položíte.',
                      },
                    }}
                  >
                    {visible.map((event, index) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        data={data}
                        gap={filter === 'all' && index > 0 ? gaps.get(event.id) : undefined}
                        conflict={isConflict(gaps.get(event.id))}
                        last={index === visible.length - 1}
                        sortable={sortable}
                        canEdit={canEdit}
                        dimmed={owner !== anyone && ownerKey(event) !== owner}
                        menu={canEdit ? menuFor(event, group) : []}
                        onOpen={() => openEvent(event)}
                      />
                    ))}
                  </SortableDataList>
                </section>
              )
            })}
            {groups.every(
              (group) => !group.events.some((event) => matchesFilter(event, filter, gaps)),
            ) ? (
              <div className="space-y-3 px-6 py-10 text-center">
                <p className="font-semibold">Žádné události v tomto přehledu</p>
                <Button
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => setFilter('all')}
                >
                  Zobrazit celý den
                </Button>
              </div>
            ) : null}
          </Card>

          {canEdit && removed.length > 0 ? (
            <RemovedEvents
              events={removed}
              onRestore={(event) => {
                setSchedule((schedule) => replaceById(schedule, event.id, { removed: false }))
                showToast({ title: 'Událost vrácena', description: event.title, tone: 'success' })
              }}
            />
          ) : null}
        </>
      )}

      {sheets}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        heading="Smazat událost?"
        description={`${deleting?.title ?? ''} je vaše vlastní událost, smazáním zmizí z harmonogramu i z webu.`}
        keepLabel="Ponechat událost"
        confirmLabel="Smazat událost"
        onConfirm={() => {
          if (!deleting) return
          setSchedule((schedule) => schedule.filter((item) => item.id !== deleting.id))
          showToast({ title: 'Událost smazána', description: deleting.title })
        }}
      />
    </Screen>
  )
}

/**
 * One event on the timeline: handle, time, a marker on the day's line (people
 * for guest events), title with place and who handles it, and the "⋯" menu.
 * The gap to the previous event sits above it, on the line.
 */
function EventRow({
  event,
  data,
  gap,
  conflict,
  last,
  sortable,
  canEdit,
  dimmed,
  menu,
  onOpen,
}: {
  event: ScheduleEvent
  data: WeddingData
  gap: Gap | undefined
  conflict: boolean
  last: boolean
  sortable: boolean
  canEdit: boolean
  dimmed: boolean
  menu: ItemMenuEntry[]
  onOpen: () => void
}) {
  const guests = event.audience === 'guests'
  const owner = ownerOf(event.owner, data)
  return (
    <SortableDataListItem
      id={event.id}
      disabled={!sortable}
      className={cn('transition-opacity', dimmed && 'opacity-35')}
    >
      {gap ? <GapLine gap={gap} sortable={sortable} /> : null}
      <div className="group flex items-stretch gap-1 sm:gap-3">
        {sortable ? (
          <SortableDataListHandle
            label={`Přesunout ${event.title}`}
            className="mt-2.5 hidden size-9 self-start opacity-50 group-hover:opacity-100 sm:grid"
          />
        ) : null}
        <div className="w-14 shrink-0 pt-3 text-right tabular-nums sm:w-20">
          {event.time ? (
            <>
              <span
                className={cn(
                  'block text-base font-semibold sm:text-lg',
                  conflict ? 'text-warning-foreground' : 'text-action',
                )}
              >
                {event.time}
              </span>
              {event.endTime ? (
                <span className="block text-xs text-muted-foreground">do {event.endTime}</span>
              ) : null}
            </>
          ) : (
            <span className="block pt-1 text-xs text-muted-foreground">Bez času</span>
          )}
        </div>
        <div aria-hidden="true" className="relative flex w-7 shrink-0 justify-center">
          <span className="absolute inset-y-0 w-px bg-border" />
          {guests ? (
            <span className="relative mt-3 grid size-7 place-items-center rounded-full bg-action text-action-foreground ring-4 ring-card">
              <Users className="size-3.5" />
            </span>
          ) : (
            <span className="relative mt-5 size-2.5 rounded-full border-2 border-action bg-card ring-4 ring-card" />
          )}
        </div>
        <div
          className={cn(
            'flex min-w-0 flex-1 items-start gap-1 rounded-xl py-1 pl-2 transition-colors sm:pl-3',
            guests && 'my-0.5 bg-panel-accent/60',
            !last && !guests && 'border-b border-border/60 rounded-none',
          )}
        >
          <button
            type="button"
            onClick={onOpen}
            className="min-w-0 flex-1 rounded-control py-2 pr-2 text-left focus-visible:outline-2 focus-visible:outline-ring"
          >
            <span className="flex items-center gap-2">
              <span className="break-words font-semibold">{event.title}</span>
              {guests ? <span className="sr-only">Uvidí i hosté.</span> : null}
            </span>
            {event.description ? (
              <span className="mt-0.5 block break-words text-sm text-muted-foreground">
                {event.description}
              </span>
            ) : null}
            {event.location || owner ? (
              <span className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {event.location ? (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="size-3.5" aria-hidden="true" />
                    {event.location}
                  </span>
                ) : null}
                {owner ? (
                  <span className="inline-flex items-center gap-1.5">
                    {owner.type === 'member' ? (
                      <Avatar
                        alt=""
                        size="sm"
                        fallback={owner.initials}
                        className="size-5 text-[0.6rem]"
                      />
                    ) : (
                      <Store className="size-3.5" aria-hidden="true" />
                    )}
                    {owner.name}
                  </span>
                ) : null}
              </span>
            ) : null}
          </button>
          {canEdit && menu.length > 0 ? (
            <div className="pt-1 pr-1">
              <ItemMenu entries={menu} label={`Možnosti události ${event.title}`} />
            </div>
          ) : null}
        </div>
      </div>
    </SortableDataListItem>
  )
}

/** The note between two events on the day's line: free time, an overlap or a wrong order. */
function GapLine({ gap, sortable }: { gap: Gap; sortable: boolean }) {
  const text =
    gap.type === 'free'
      ? `${formatDuration(gap.minutes)} volno`
      : gap.type === 'overlap'
        ? `Překrývá se s událostí ${gap.with.title}${gap.with.endTime ? ` (do ${gap.with.endTime})` : ''}`
        : `Začíná dřív než ${gap.with.title} (${gap.with.time})`
  return (
    <div className="flex items-center gap-1 sm:gap-3">
      {sortable ? <span className="hidden w-9 shrink-0 sm:block" /> : null}
      <span className="w-14 shrink-0 sm:w-20" />
      <span aria-hidden="true" className="relative flex w-7 shrink-0 justify-center self-stretch">
        <span
          className={cn(
            'absolute inset-y-0 w-px',
            gap.type === 'free'
              ? 'border-l border-dashed border-border bg-transparent'
              : 'bg-border',
          )}
        />
      </span>
      <span
        className={cn(
          'inline-flex items-center gap-1.5 py-1.5 pl-3 text-xs',
          gap.type === 'free' ? 'text-muted-foreground' : 'font-semibold text-warning-foreground',
        )}
      >
        {gap.type === 'free' ? null : <TriangleAlert className="size-3.5" aria-hidden="true" />}
        {text}
      </span>
    </div>
  )
}

/** Section events the couple took off the day; each can come back. */
function RemovedEvents({
  events,
  onRestore,
}: {
  events: ScheduleEvent[]
  onRestore: (event: ScheduleEvent) => void
}) {
  return (
    <details className="group rounded-panel border border-border bg-card px-5 py-3 sm:px-6">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold">
        <ChevronRight
          className="size-4 text-muted-foreground transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        Odebrané ze sekcí
        <span className="font-normal tabular-nums text-muted-foreground">{events.length}</span>
      </summary>
      <p className="mt-2 text-sm text-muted-foreground">
        Události ze sekcí, které jste z průběhu dne vyřadili. Sekce samotné zůstávají beze změny.
      </p>
      <ul className="mt-3 divide-y divide-border">
        {events.map((event) => (
          <li key={event.id} className="flex items-center gap-3 py-2.5">
            <span className="w-12 shrink-0 text-sm tabular-nums text-muted-foreground">
              {timeRange(event) ? event.time : '—'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">{event.title}</span>
              {event.source ? (
                <span className="block text-xs text-muted-foreground">
                  Ze sekce {event.source.label}
                </span>
              ) : null}
            </span>
            <Button appearance="ghost" tone="neutral" size="sm" onClick={() => onRestore(event)}>
              Vrátit
            </Button>
          </li>
        ))}
      </ul>
    </details>
  )
}
